import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Box,
  Typography,
  Alert,
} from '@mui/material';

interface BusinessInfoConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentDescription?: string;
  currentProducts?: string[];
  newDescription?: string;
  newProducts?: string;
}

const BusinessInfoConfirmationDialog: React.FC<BusinessInfoConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  currentDescription,
  currentProducts,
  newDescription,
  newProducts,
}) => {
  const hasCurrentDescription = currentDescription && currentDescription.trim().length > 0;
  const hasCurrentProducts = currentProducts && currentProducts.length > 0;
  const hasNewDescription = newDescription && newDescription.trim().length > 0;
  const hasNewProducts = newProducts && newProducts.trim().length > 0;

  // Show dialog only if there's existing data to replace
  const shouldShowDescription = hasCurrentDescription;
  const shouldShowProducts = hasCurrentProducts;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
          '@media (max-width: 600px)': {
            margin: 1,
            maxWidth: 'calc(100% - 16px)',
          },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="div">
          Update Existing Information?
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2 }}>
        <DialogContentText sx={{ mb: 3 }}>
          We found existing information in your profile. The following will be updated:
        </DialogContentText>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            maxHeight: '50vh',
            overflowY: 'auto',
            '@media (max-width: 600px)': {
              maxHeight: '40vh',
            },
          }}
        >
          {shouldShowDescription && (
            <Alert
              severity="info"
              sx={{
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Description
              </Typography>
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}
                >
                  Current:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.9rem',
                    color: 'text.primary',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    p: 1,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                  }}
                >
                  {currentDescription}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}
                >
                  New (will replace current):
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.9rem',
                    color: 'text.primary',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    p: 1,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                  }}
                >
                  {hasNewDescription ? newDescription : '(No description provided)'}
                </Typography>
              </Box>
            </Alert>
          )}
          {shouldShowProducts && (
            <Alert
              severity="info"
              sx={{
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Products/Services
              </Typography>
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}
                >
                  Current:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.9rem',
                    color: 'text.primary',
                    wordBreak: 'break-word',
                    p: 1,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                  }}
                >
                  {currentProducts.join(', ')}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}
                >
                  New items to add:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.9rem',
                    color: 'text.primary',
                    wordBreak: 'break-word',
                    p: 1,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                  }}
                >
                  {hasNewProducts && newProducts
                    ? newProducts
                        .split(',')
                        .map((p: string) => p.trim())
                        .filter((p: string) => p && !currentProducts.map((ex: string) => ex.toLowerCase().trim()).includes(p.toLowerCase().trim()))
                        .join(', ') || 'No new items (all already exist)'
                    : '(No products/services provided)'}
                </Typography>
              </Box>
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          '& > button': {
            width: { xs: '100%', sm: 'auto' },
            minWidth: { xs: '100%', sm: 120 },
          },
        }}
      >
        <Button onClick={onClose} variant="outlined" fullWidth={false}>
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" color="primary" fullWidth={false}>
          Update Information
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BusinessInfoConfirmationDialog;

